/**
   * VueAnimXyz v0.4.1
   * Copyright (c) 2020-present Ingram Projects
   * Released under the MIT License.
   * https://animxyz.com
   */
  
import { mergeData as mergeData$1 } from 'vue-functional-data-merge';
import clsx from 'clsx';

function getXyzDurationForMode (mode, duration) {
  switch (typeof duration) {
    case 'number':
      return duration;

    case 'string':
      return duration;

    case 'object':
      if (duration === null) return null;
      return duration[mode];
  }

  return null;
}

function getXyzElementMode (el) {
  if (el.classList.contains('xyz-appear')) {
    return 'appear';
  }

  if (el.classList.contains('xyz-in')) {
    return 'in';
  }

  if (el.classList.contains('xyz-out')) {
    return 'out';
  }

  return null;
}

function clearXyzElementProperties(el) {
  if (el._xyzAppearObserver) {
    el._xyzAppearObserver.disconnect();
  }

  if (el._xyzAnimTimeout) {
    clearTimeout(el._xyzAnimTimeout);
  }

  el.removeEventListener('animationend', el._xyzAnimEnd);
  el.removeEventListener('animationcancelled', el._xyzAnimEnd);
  delete el._xyzAppearObserver;
  delete el._xyzAnimTimeout;
  delete el._xyzAnimEnd;
}

function getXyzAnimationHook (duration, appearVisible) {
  return (el, done) => {
    clearXyzElementProperties(el);

    function xyzAnimDone() {
      clearXyzElementProperties(el);
      done();
    }

    const mode = getXyzElementMode(el);

    function runAnim() {
      const modeDuration = getXyzDurationForMode(mode, duration);

      if (typeof modeDuration === 'number') {
        el._xyzAnimTimeout = setTimeout(xyzAnimDone, modeDuration);
        return;
      }

      const xyzModeKeyframes = "xyz-".concat(mode, "-keyframes");
      const xyzEls = new Set([el]);

      if (modeDuration === 'auto') {
        const xyzNestedEls = el.querySelectorAll(".xyz-nested, .xyz-".concat(mode, "-nested"));
        xyzNestedEls.forEach(xyzEls.add, xyzEls);
      }

      function removeXyzEl(xyzEl) {
        xyzEls.delete(xyzEl);

        if (xyzEls.size === 0) {
          xyzAnimDone();
        }
      } // After one tick remove any elements that are dont have active animations


      el._xyzAnimTimeout = setTimeout(() => {
        xyzEls.forEach(xyzEl => {
          // Remove if element isnt visible
          const visible = xyzEl.offsetParent || xyzEl.getClientRects().length;

          if (!visible) {
            removeXyzEl(xyzEl);
          } // Remove if element has xyz animation overridden


          const animationName = window.getComputedStyle(xyzEl).getPropertyValue('animation-name');

          if (animationName.indexOf(xyzModeKeyframes) === -1) {
            removeXyzEl(xyzEl);
          }
        });
      });

      el._xyzAnimEnd = event => {
        if (event.animationName === xyzModeKeyframes) {
          removeXyzEl(event.target);
        }
      };

      el.addEventListener('animationend', el._xyzAnimEnd, false);
      el.addEventListener('animationcancelled', el._xyzAnimEnd, false);
    }

    if (mode === 'appear' && appearVisible) {
      const observerOptions = Object.assign({}, appearVisible);
      el.classList.add('xyz-paused');
      el._xyzAppearObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            el.classList.remove('xyz-paused');
            observer.disconnect();
            runAnim();
          }
        });
      }, observerOptions);

      el._xyzAppearObserver.observe(el);
    } else {
      runAnim();
    }
  };
}

var xyzTransitionClasses = {
  appearFrom: 'xyz-appear-from',
  appearActive: 'xyz-appear',
  appearTo: 'xyz-appear-to',
  inFrom: 'xyz-in-from',
  inActive: 'xyz-in',
  inTo: 'xyz-in-to',
  outFrom: 'xyz-out-from',
  outActive: 'xyz-out',
  outTo: 'xyz-out-to'
};

const xyzTransitionProps = {
  appear: Boolean,
  appearVisible: [Boolean, Object],
  duration: [Number, String, Object],
  mode: String,
  appearClass: String,
  appearActiveClass: String,
  appearToClass: String,
  enterClass: String,
  enterActiveClass: String,
  enterToClass: String,
  leaveClass: String,
  leaveActiveClass: String,
  leaveToClass: String
};
const xyzTransitionGroupProps = Object.assign({}, xyzTransitionProps, {
  tag: {
    type: String,
    default: 'div'
  },
  moveClass: String
});
function mergeData(data1 = {}, data2 = {}) {
  return mergeData$1(data1, data2);
}

function deleteUndefined(obj) {
  Object.keys(obj).forEach(key => {
    if (obj[key] === undefined) {
      delete obj[key];
    }
  });
}

function getXyzTransitionData(data) {
  deleteUndefined(data.attrs);
  data.attrs.appear = Boolean(data.attrs.appear || data.attrs.appearVisible);
  const {
    appear,
    appearVisible,
    duration
  } = data.attrs;
  const animationHook = getXyzAnimationHook(duration, appearVisible);
  const transitionData = {
    attrs: {
      css: true,
      type: 'animation',
      appearClass: xyzTransitionClasses.appearFrom,
      appearActiveClass: xyzTransitionClasses.appearActive,
      appearToClass: xyzTransitionClasses.appearTo,
      enterClass: xyzTransitionClasses.inFrom,
      enterActiveClass: xyzTransitionClasses.inActive,
      enterToClass: xyzTransitionClasses.inTo,
      leaveClass: xyzTransitionClasses.outFrom,
      leaveActiveClass: xyzTransitionClasses.outActive,
      leaveToClass: xyzTransitionClasses.outTo
    },
    on: {
      enter: animationHook,
      leave: animationHook
    }
  };

  if (appear) {
    transitionData.on.appear = animationHook;
  }

  const mergedData = mergeData(transitionData, data);
  delete mergedData.attrs.duration;
  return mergedData;
}

var XyzTransition = {
  name: 'XyzTransition',
  functional: true,
  props: xyzTransitionProps,

  render(createElement, context) {
    const {
      data = {},
      props = {},
      children = []
    } = context;
    const newData = getXyzTransitionData(Object.assign({}, data, {
      attrs: Object.assign({}, data.attrs, props)
    }));
    children.forEach(node => {
      // Iterate through children and merge transition directives and styles
      node.data = mergeData(data, node.data);
    });
    return createElement('transition', newData, children);
  }

};

function getTransitionRawChildren(children) {
  return children.filter(node => {
    return node.tag && node.key != null && String(node.key).indexOf('__vlist') !== 0;
  });
}

var XyzTransitionGroup = {
  name: 'XyzTransitionGroup',
  functional: true,
  props: xyzTransitionGroupProps,

  render(createElement, context) {
    const {
      data = {},
      props = {},
      children = []
    } = context;
    const newData = getXyzTransitionData(Object.assign({}, data, {
      attrs: Object.assign({}, data.attrs, props)
    }));
    const rawChildren = getTransitionRawChildren(children);
    rawChildren.forEach((node, index) => {
      // Iterate through children and apply xyz indexes
      node.data = mergeData({
        staticStyle: {
          '--xyz-index': index,
          '--xyz-index-rev': children.length - index - 1
        }
      }, node.data);
    });
    return createElement('transition-group', newData, children);
  }

};

function updateDirective(el, {
  value
}) {
  el.setAttribute('xyz', clsx(el._xyzOriginal, value));
}

var xyz = {
  bind(el) {
    el._xyzOriginal = el.getAttribute('xyz');
    updateDirective(...arguments);
  },

  update: updateDirective,

  unbind(el, binding, vnode, oldVnode, isDestroy) {
    if (!isDestroy) {
      el.setAttribute('xyz', el._xyzOriginal);
    }
  }

};

const VueAnimXyz = {
  install(Vue) {
    Vue.component('XyzTransition', XyzTransition);
    Vue.component('XyzTransitionGroup', XyzTransitionGroup);
    Vue.directive('xyz', xyz);
  }

}; // Auto-install when vue is found (eg. in browser via <script> tag)

let GlobalVue = null;

if (typeof window !== 'undefined') {
  GlobalVue = window.Vue;
} else if (typeof global !== 'undefined') {
  GlobalVue = global.Vue;
}

if (GlobalVue) {
  GlobalVue.use(VueAnimXyz);
}

export default VueAnimXyz;
export { XyzTransition, XyzTransitionGroup, xyz };
